import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Ban, MoreVertical, Eye, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import adminApi from "@/lib/adminApi";
import { AdjustOwnerCostDialog } from "@/components/NewIPhonesTab";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const fmtDate = (s) => (s || "").slice(0, 16).replace("T", " ") || "—";
const emptyFilters = { date_from: "", date_to: "", model: "", capacity: "", seller: "", payment_method: "", item_type: "" };

const typeMeta = (r) =>
  r.reason === "TRADE-IN" ? ["TRADE-IN", "bg-indigo-100 text-indigo-700"]
  : r.type === "ACCESORIO" ? ["ACCESORIO", "bg-amber-100 text-amber-700"]
  : r.type === "NUEVO" ? ["IPHONE NUEVO", "bg-blue-100 text-blue-700"]
  : ["VENTA SIMPLE", "bg-emerald-100 text-emerald-700"];

function SalesKpis({ s }) {
  const items = [
    ["Total de ventas", `${s.units}`, `${s.venta_units} venta simple · ${s.tradein_units} trade-in`],
    ["Equipos vendidos", `${s.units}`, "unidades"],
    ["Ingresos", money(s.revenue), "precio de venta"],
    ["Costo adquisición", money(s.cost), "inversión"],
    ["Utilidad total", money(s.profit), "ingresos − costo"],
    ["Margen promedio", `${s.margin_pct}%`, "utilidad / ingresos"],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      {items.map(([l, v, sub], i) => (
        <div key={l} className="rounded-2xl border border-zinc-200 p-3" data-testid={`sales-kpi-${i}`}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">{l}</p>
          <p className={`text-lg font-semibold ${l === "Utilidad total" ? (s.profit >= 0 ? "text-emerald-600" : "text-red-600") : "text-zinc-900"}`}>{v}</p>
          <p className="text-[10px] text-zinc-400">{sub}</p>
        </div>
      ))}
    </div>
  );
}

function SalesFilters({ f, setF, options, q, setQ, onSearch }) {
  const sel = (k, label, opts) => (
    <select data-testid={`sales-filter-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm bg-white">
      <option value="">{label}</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="sales-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="Buscar ID inventario, IMEI, modelo, cliente..." className="h-11 rounded-xl pl-9" />
        </div>
        <Button variant="outline" onClick={onSearch} className="h-11 rounded-xl" data-testid="sales-search-btn">Buscar</Button>
        <Button variant="ghost" onClick={() => { setF(emptyFilters); setQ(""); }} className="h-11 rounded-xl" data-testid="sales-filters-clear">Limpiar</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Desde</span><Input type="date" data-testid="sales-filter-date_from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 rounded-xl" /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Hasta</span><Input type="date" data-testid="sales-filter-date_to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 rounded-xl" /></label>
        {sel("model", "Todos los modelos", options.models)}
        {sel("capacity", "Todas las capacidades", options.capacities)}
        {sel("seller", "Todos los usuarios", options.sellers)}
        {sel("payment_method", "Todos los métodos", options.payment_methods)}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="sales-type-filter">
        {[["", "Todos"], ["IPHONE", "iPhone usados"], ["NUEVO", "iPhone nuevos"], ["ACCESORIO", "Accesorios"]].map(([k, label]) => (
          <button key={k || "all"} data-testid={`sales-type-${k || "all"}`} onClick={() => setF({ ...f, item_type: k })}
            className={`h-8 px-4 rounded-full text-xs font-medium transition-colors ${f.item_type === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{label}</button>
        ))}
      </div>
    </div>
  );
}

// Elegibilidad de acciones (reutilizada en tabla desktop y tarjetas móvil)
const canAnular = (r, canVoid) => canVoid && !r.voided && (r.type !== "NUEVO" || (r.reason !== "TRADE-IN" && !!r.new_id));
const canAdjustar = (r, canVoid) => canVoid && r.type === "NUEVO" && !!r.owner_name && !r.voided;

function OptionsMenu({ r, canVoid, onVoid, onAdjust, onDetails }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`sales-options-${r.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-medium">
          <MoreVertical className="w-3.5 h-3.5" /> Opciones
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem data-testid={`sales-details-${r.id}`} onClick={() => onDetails(r)}><Eye className="w-3.5 h-3.5 mr-2" /> Ver detalles</DropdownMenuItem>
        {canAdjustar(r, canVoid) && <DropdownMenuItem data-testid={`sales-adjust-${r.id}`} onClick={() => onAdjust(r)}><Pencil className="w-3.5 h-3.5 mr-2" /> Corregir monto</DropdownMenuItem>}
        {canAnular(r, canVoid) && <DropdownMenuItem data-testid={`sales-void-${r.id}`} onClick={() => onVoid(r)} className="text-red-600 focus:text-red-700"><Ban className="w-3.5 h-3.5 mr-2" /> Anular venta</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusBadge({ r }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.voided ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`} data-testid={`sales-status-${r.id}`} title={r.voided ? `Anulada por ${r.voided_by || ""}: ${r.void_reason || ""}` : ""}>{r.voided ? "ANULADA" : "REALIZADA"}</span>
  );
}

function SalesList({ rows, canVoid, onVoid, onAdjust, onDetails }) {
  if (rows.length === 0) return <p className="text-sm text-zinc-400 py-8 text-center" data-testid="sales-empty">Sin ventas para los filtros seleccionados.</p>;
  return (
    <>
      {/* DESKTOP — tabla compacta, sin scroll horizontal */}
      <div className="hidden md:block rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-xs table-fixed" data-testid="sales-table">
          <thead className="bg-zinc-50 text-zinc-500 text-left">
            <tr>
              <th className="px-3 py-2 font-medium w-[120px]">Fecha</th>
              <th className="px-3 py-2 font-medium">Equipo / venta</th>
              <th className="px-3 py-2 font-medium w-[150px]">Cliente</th>
              <th className="px-3 py-2 font-medium w-[90px] text-right">Monto</th>
              <th className="px-3 py-2 font-medium w-[96px]">Estado</th>
              <th className="px-3 py-2 font-medium w-[120px] text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const [tl, tc] = typeMeta(r);
              return (
                <tr key={r.id + (r.voided_at || "")} className={`border-t border-zinc-100 hover:bg-zinc-50 align-top ${r.voided ? "opacity-60" : ""}`} data-testid={`sales-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{fmtDate(r.at)}</td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-zinc-900 truncate">{r.model} <span className="font-normal text-zinc-400">{r.type === "ACCESORIO" ? `×${r.qty}` : (r.capacity || "")}</span></p>
                    <p className="text-[10px] text-zinc-400 truncate font-mono">{r.id}{r.imei ? ` · ${r.imei}` : ""}</p>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[9px] font-semibold ${tc}`}>{tl}</span>
                  </td>
                  <td className="px-3 py-2 truncate">{r.customer || "—"}{r.owner_name ? <span className="block text-[10px] text-zinc-400 truncate">Consig.: {r.owner_name}</span> : null}</td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{money(r.sale_price)}</td>
                  <td className="px-3 py-2"><StatusBadge r={r} /></td>
                  <td className="px-3 py-2 text-right"><OptionsMenu r={r} canVoid={canVoid} onVoid={onVoid} onAdjust={onAdjust} onDetails={onDetails} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MÓVIL — tarjetas compactas, sin overflow */}
      <div className="md:hidden space-y-2.5" data-testid="sales-cards">
        {rows.map((r) => {
          const [tl, tc] = typeMeta(r);
          return (
            <div key={r.id + (r.voided_at || "")} data-testid={`sales-card-${r.id}`} className={`rounded-2xl border border-zinc-200 bg-white p-3.5 ${r.voided ? "opacity-70" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-900 break-words leading-snug">{r.model} <span className="font-normal text-zinc-400">{r.type === "ACCESORIO" ? `×${r.qty}` : (r.capacity || "")}</span></p>
                  <p className="text-[10px] text-zinc-400 font-mono break-all">{r.id}{r.imei ? ` · ${r.imei}` : ""}</p>
                </div>
                <StatusBadge r={r} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${tc}`}>{tl}</span>
                <span>{fmtDate(r.at)}</span>
                <span className="font-semibold text-zinc-900">{money(r.sale_price)}</span>
              </div>
              <div className="mt-1.5 text-xs text-zinc-500 space-y-0.5">
                <p className="break-words">Cliente: <span className="text-zinc-700">{r.customer || "—"}</span></p>
                {r.owner_name && <p className="break-words">Consignador: <span className="text-zinc-700">{r.owner_name}</span></p>}
              </div>
              <div className="mt-2.5 flex justify-end">
                <OptionsMenu r={r} canVoid={canVoid} onVoid={onVoid} onAdjust={onAdjust} onDetails={onDetails} />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function SaleDetailsDialog({ r, onClose }) {
  if (!r) return null;
  const [tl] = typeMeta(r);
  const Row = ({ k, v }) => <div className="flex justify-between gap-3 py-1 border-b border-zinc-100 last:border-0"><span className="text-zinc-500">{k}</span><span className="font-medium text-zinc-800 text-right break-words">{v ?? "—"}</span></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="sale-details-dialog">
        <DialogHeader>
          <DialogTitle>Detalle de la venta</DialogTitle>
          <DialogDescription className="font-mono">{r.id}</DialogDescription>
        </DialogHeader>
        <div className="text-sm">
          <Row k="Tipo" v={tl} />
          <Row k="Equipo / producto" v={`${r.model || ""} ${r.type === "ACCESORIO" ? `×${r.qty}` : (r.capacity || "")}`.trim()} />
          <Row k="IMEI / SKU" v={r.imei} />
          <Row k="Color" v={r.color} />
          {r.owner_name && <Row k="Consignador" v={r.owner_name} />}
          <Row k="Cliente" v={r.customer} />
          <Row k="Teléfono cliente" v={r.customer_phone} />
          <Row k="Fecha" v={fmtDate(r.at)} />
          <Row k="Usuario" v={r.by} />
          <Row k="Método de pago" v={r.payment_method} />
          <Row k="Costo" v={r.type === "NUEVO" && r.cost_pending ? "Pendiente" : money(r.cost)} />
          <Row k="Venta" v={money(r.sale_price)} />
          <Row k="Utilidad" v={money(r.profit)} />
          <Row k="Margen" v={r.margin_pct != null ? `${r.margin_pct}%` : "—"} />
          <Row k="Estado" v={r.voided ? "ANULADA" : "REALIZADA"} />
          {r.voided && <><Row k="Anulada por" v={r.voided_by} /><Row k="Motivo" v={r.void_reason} /></>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const SalesTab = () => {
  const { admin } = useAuth();
  const canVoid = admin?.role === "admin";
  const [f, setF] = useState(emptyFilters);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [adjItem, setAdjItem] = useState(null);
  const [details, setDetails] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [vstatus, setVstatus] = useState("realizadas");
  const [quick, setQuick] = useState("todo");
  const [fowner, setFowner] = useState("all");
  const load = (filters = f, search = q) => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (search.trim()) params.q = search.trim();
    adminApi.get("/admin/inventory/sales-report", { params }).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar Ventas"));
  };
  useEffect(() => { load(f, q); /* eslint-disable-next-line */ }, [f]);
  const confirmVoid = async () => {
    if (!reason.trim()) { toast.error("El motivo de anulación es obligatorio"); return; }
    setSaving(true);
    try {
      if (target.reason === "TRADE-IN") {
        if (!target.operation_id) { toast.error("No se encontró la operación de Trade-In para anular"); setSaving(false); return; }
        await adminApi.post(`/admin/quotes/${target.operation_id}/void-trade-in`, { reason });
      } else if (target.type === "ACCESORIO") {
        await adminApi.post(`/admin/accessories/sales/${target.id}/void`, { reason });
      } else if (target.type === "NUEVO") {
        await adminApi.post(`/admin/new-iphones/${target.new_id}/void-sale`, { reason });
      } else {
        await adminApi.post(`/admin/inventory/${target.id}/void-sale`, { reason });
      }
      toast.success("Operación anulada correctamente.");
      setTarget(null); setReason(""); load(f, q);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo anular la operación"); } finally { setSaving(false); }
  };
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  const rowDate = (r) => r.sale_date || r.sold_at || r.date || r.exit_date || r.created_at;
  const inQuick = (r) => { if (quick === "todo") return true; const d = new Date(rowDate(r)); if (isNaN(d)) return true; const now = new Date(); if (quick === "semana") return (now - d) / 86400000 <= 7; if (quick === "mes") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); if (quick === "anio") return d.getFullYear() === now.getFullYear(); return true; };
  const ownerOpts = [...new Set((data.sales || []).map((r) => r.owner_name).filter(Boolean))].sort();
  const visibleRows = (data.sales || []).filter((r) => (vstatus === "anuladas" ? r.voided : !r.voided) && (fowner === "all" || (fowner === "__RR__" ? !r.owner_name : r.owner_name === fowner)) && inQuick(r));
  return (
    <div data-testid="sales-panel" className="space-y-4">
      <SalesKpis s={data.summary} />
      <SalesFilters f={f} setF={setF} options={data.options} q={q} setQ={setQ} onSearch={() => load(f, q)} />
      <div className="flex flex-wrap items-center gap-2" data-testid="sales-view-controls">
        <div className="flex gap-1.5" data-testid="sales-status-tabs">
          {[["realizadas", "Realizadas"], ["anuladas", "Anuladas"]].map(([k, l]) => (
            <button key={k} data-testid={`sales-status-${k}`} onClick={() => setVstatus(k)}
              className={`h-9 px-4 rounded-full text-xs font-semibold transition-colors ${vstatus === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-1.5" data-testid="sales-quick-range">
          {[["semana", "Semana"], ["mes", "Mes"], ["anio", "Año"], ["todo", "Todo"]].map(([k, l]) => (
            <button key={k} data-testid={`sales-quick-${k}`} onClick={() => setQuick(k)}
              className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${quick === k ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{l}</button>
          ))}
        </div>
        <select data-testid="sales-owner-filter" value={fowner} onChange={(e) => setFowner(e.target.value)} className="h-9 rounded-xl border border-zinc-200 px-3 text-xs bg-white">
          <option value="all">Dueño: Todos</option>
          <option value="__RR__">PuntoCell (RR)</option>
          {ownerOpts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400" data-testid="sales-count">{visibleRows.length} operación(es){vstatus === "anuladas" ? " anuladas" : ""} · histórico total {money(data.all_time.profit)} utilidad (sin anuladas)</p>
      <SalesList rows={visibleRows} canVoid={canVoid}
        onVoid={(r) => { setTarget(r); setReason(""); }}
        onAdjust={(r) => setAdjItem({ id: r.new_id, model: r.model, capacity: r.capacity, owner_name: r.owner_name, sale: r.sale })}
        onDetails={(r) => setDetails(r)} />
      {adjItem && <AdjustOwnerCostDialog item={adjItem} onClose={() => setAdjItem(null)} onSaved={() => load(f, q)} />}
      <SaleDetailsDialog r={details} onClose={() => setDetails(null)} />

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle>¿Seguro que deseas anular esta venta?</DialogTitle>
            <DialogDescription>Esta operación revertirá la venta y sus efectos asociados cuando sea seguro hacerlo. El registro permanecerá en el historial como ANULADA.</DialogDescription>
          </DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm space-y-1">
                <p><b>{typeMeta(target)[0]}</b> · {target.model} {target.capacity || ""}</p>
                <p className="text-xs text-zinc-500 font-mono">{target.id}{target.imei ? ` · IMEI ${target.imei}` : ""}</p>
                <p className="text-xs text-zinc-600">Cliente: <b>{target.customer || "—"}</b>{target.owner_name ? <> · Consignador: <b>{target.owner_name}</b></> : null}</p>
                <p className="text-xs text-zinc-600">Fecha: {fmtDate(target.at)} · Monto real: <b>{money(target.sale_price)}</b></p>
                {target.reason === "TRADE-IN"
                  ? <p className="text-xs text-zinc-600">📤 El equipo vendido vuelve a DISPONIBLE · 📥 el equipo del cliente sale del inventario.</p>
                  : <p className="text-xs text-zinc-600">El equipo vuelve de VENDIDO a DISPONIBLE.</p>}
              </div>
              <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Motivo de anulación *</span>
                <Input data-testid="void-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej: Venta de prueba / error de captura" className="h-11 rounded-xl" /></label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setTarget(null)} className="h-11 rounded-xl flex-1" data-testid="void-cancel">Cancelar</Button>
                <Button onClick={confirmVoid} disabled={saving || !reason.trim()} className="h-11 rounded-xl flex-1 bg-red-600 hover:bg-red-700" data-testid="void-confirm">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar anulación"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
